/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { ArrowLeft, BookOpen, Compass, ExternalLink, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Input, PageHeader } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { startTour } from '@/components/Tour';
import { ARTICLES, SECTIONS, articleById } from '@/help/articles';
import { Markdown } from '@/help/Markdown';

const norm = (s: string) => s.toLowerCase().replace(/ё/g, 'е');

export function HelpPage() {
  const { hasModule } = useAuth();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    return ARTICLES.filter((a) => (!a.module || hasModule(a.module)) && words.every((w) => norm(`${a.title} ${a.summary} ${a.keywords ?? ''} ${a.body}`).includes(w)));
  }, [q, hasModule]);
  return (
    <>
      <PageHeader title="База знаний" subtitle="Подробное описание возможностей KIN DIGITAL CRM+ по разделам"
        actions={<Button variant="secondary" onClick={() => startTour()}><Compass size={16} />Тур по CRM</Button>} />
      <div className="relative mb-5 max-w-xl"><Search size={16} className="absolute left-3 top-2.5 text-slate-400" /><Input className="pl-9" placeholder="Поиск: например «срок апелляции», «подпись», «реестр Почты»" value={q} onChange={(e) => setQ(e.target.value)} autoFocus /></div>
      {!list.length && <p className="text-sm text-slate-500">Ничего не найдено. Попробуйте другое слово.</p>}
      <div className="space-y-6">
        {SECTIONS.map((s) => {
          const items = list.filter((a) => a.section === s);
          if (!items.length) return null;
          return (
            <section key={s}>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{s}</h2>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{items.map((a) => (
                <Link key={a.id} to={`/help/${a.id}`} className="card block p-4 transition hover:border-brand-300 hover:shadow">
                  <div className="flex items-center gap-2 font-medium text-slate-900"><BookOpen size={16} className="shrink-0 text-brand-600" />{a.title}</div>
                  <p className="mt-1 text-sm text-slate-500">{a.summary}</p>
                </Link>
              ))}</div>
            </section>
          );
        })}
      </div>
    </>
  );
}

export function HelpArticlePage() {
  const { id } = useParams();
  const a = articleById(id ?? '');
  if (!a) return <div className="text-sm text-slate-500">Статья не найдена. <Link className="text-brand-700 underline" to="/help">К списку</Link></div>;
  const same = ARTICLES.filter((x) => x.section === a.section && x.id !== a.id);
  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/help" className="mb-3 inline-flex items-center gap-1 text-sm text-brand-700 hover:underline"><ArrowLeft size={14} />База знаний</Link>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{a.section}</div>
      <h1 className="mb-1 text-2xl font-semibold text-slate-900">{a.title}</h1>
      <p className="mb-4 text-slate-500">{a.summary}</p>
      {a.path && <Link to={a.path} className="mb-5 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"><ExternalLink size={14} />Открыть раздел</Link>}
      <div className="card p-5"><Markdown text={a.body} /></div>
      {same.length > 0 && <div className="mt-6"><div className="mb-2 text-sm font-medium">Ещё в разделе «{a.section}»</div>
        <ul className="space-y-1 text-sm">{same.map((x) => <li key={x.id}><Link className="text-brand-700 hover:underline" to={`/help/${x.id}`}>{x.title}</Link></li>)}</ul></div>}
    </div>
  );
}
